import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { receiptPaymentStatus } from '../api';
import { CheckCircle2, XCircle, Loader2, ArrowLeft, Home } from 'lucide-react';

const ReceiptPaymentStatus = ({ outcome }) => {
  const { lang, dir } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const receiptId = params.get('receipt_id');
  const [status, setStatus] = useState(outcome === 'cancel' ? { payment_status: 'canceled' } : null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (outcome === 'cancel' || !sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await receiptPaymentStatus(sessionId);
        if (cancelled) return;
        setStatus(r);
        setAttempts((n) => n + 1);
        if (r.payment_status !== 'paid' && attempts < 30) {
          setTimeout(poll, 2000);
        }
      } catch (e) {
        setStatus({ payment_status: 'error', error: e?.response?.data?.detail || 'Failed' });
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const paid = status?.payment_status === 'paid';
  const canceled = status?.payment_status === 'canceled';
  const errored = status?.payment_status === 'error';
  const pending = !paid && !canceled && !errored;

  return (
    <div dir={dir} className="min-h-screen pt-24 pb-16 bg-[#f8fafc] flex items-start justify-center px-6">
      <div className="w-full max-w-md" data-testid="receipt-payment-status">
        <div className="glass-card rounded-2xl p-8 text-center">
          {pending && (
            <>
              <Loader2 className="w-14 h-14 text-[#2563eb] mx-auto mb-4 animate-spin" />
              <h1 className="font-display text-2xl text-[#0a0a0a] mb-2">{lang === 'ar' ? 'يتم تأكيد الدفع…' : 'Confirming payment…'}</h1>
              <p className="text-sm text-[#111827]/60">{lang === 'ar' ? 'قد يستغرق هذا بضع ثوانٍ. لا تُغلق النافذة.' : 'This may take a few seconds. Please don\'t close this window.'}</p>
            </>
          )}
          {paid && (
            <>
              <CheckCircle2 className="w-16 h-16 text-blue-600 mx-auto mb-4" data-testid="paid-icon" />
              <h1 className="font-display text-2xl text-[#0a0a0a] mb-2">{lang === 'ar' ? 'تم الدفع بنجاح!' : 'Payment successful!'}</h1>
              <p className="text-sm text-[#111827]/60 mb-2">{lang === 'ar' ? 'تم تحديث حالة الإيصال إلى مدفوع.' : 'The receipt has been marked as paid.'}</p>
              {status?.amount && (
                <div className="text-2xl font-bold text-blue-700 mt-3 tabular-nums">
                  {Number(status.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} {(status.currency || 'aed').toUpperCase()}
                </div>
              )}
              {receiptId && (
                <div className="text-xs text-[#111827]/50 mt-2 font-mono">{receiptId}</div>
              )}
            </>
          )}
          {canceled && (
            <>
              <XCircle className="w-16 h-16 text-amber-600 mx-auto mb-4" />
              <h1 className="font-display text-2xl text-[#0a0a0a] mb-2">{lang === 'ar' ? 'تم إلغاء الدفع' : 'Payment canceled'}</h1>
              <p className="text-sm text-[#111827]/60">{lang === 'ar' ? 'لم يتم خصم أي مبلغ. يمكنك المحاولة مرة أخرى.' : 'No amount was charged. You can try again.'}</p>
            </>
          )}
          {errored && (
            <>
              <XCircle className="w-16 h-16 text-rose-600 mx-auto mb-4" />
              <h1 className="font-display text-2xl text-[#0a0a0a] mb-2">{lang === 'ar' ? 'حدث خطأ' : 'Something went wrong'}</h1>
              <p className="text-sm text-[#111827]/60">{status?.error || (lang === 'ar' ? 'يرجى التواصل مع الدعم.' : 'Please contact support.')}</p>
            </>
          )}
          <div className="mt-6 flex gap-2 justify-center">
            <Link to="/">
              <Button className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent">
                <Home className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'الرئيسية' : 'Home'}
              </Button>
            </Link>
            {!paid && receiptId && (
              <Link to="/MyApplications">
                <Button className="gold-button rounded-md h-10 px-4 text-xs">
                  <ArrowLeft className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'طلباتي' : 'My Applications'}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptPaymentStatus;
